import { socialNetworkTypeIdToChartKey } from "./messagesReport"
import { formatLocalDateKey } from "../../utils/dateRangeUtils"

export const DEMOGRAPHIC_TYPE = {
  CITY: 1,
  COUNTRY: 2,
  GENDER_AGE: 3,
}

const AGE_BUCKETS = ["13-17", "18-24", "25-34", "35-44", "45-54", "55-64", "65+"]

export function formatDemographicsReportDate(date) {
  return formatLocalDateKey(date)
}

export function buildDemographicsFollowersFetchKey(clientId, startDate, endDate) {
  return `${String(clientId ?? "")}|${startDate ?? ""}|${endDate ?? ""}`
}

export function decodeDemographicName(name) {
  const raw = String(name ?? "").trim()
  if (!raw) return ""
  if (!raw.includes("\\u")) return raw
  try {
    return JSON.parse(`"${raw.replace(/"/g, '\\"')}"`)
  } catch {
    return raw
  }
}

function isNumericPlaceName(name) {
  return /^\d+$/.test(String(name ?? "").trim())
}

function normalizePlaceKey(name) {
  return decodeDemographicName(name).trim().toLowerCase()
}

let intlCountryNameMap = null

function getIntlCountryNameMap() {
  if (intlCountryNameMap) return intlCountryNameMap
  intlCountryNameMap = {}
  if (typeof Intl === "undefined" || typeof Intl.DisplayNames !== "function") {
    return intlCountryNameMap
  }
  try {
    const names = new Intl.DisplayNames(["en"], { type: "region" })
    for (let first = 65; first <= 90; first += 1) {
      for (let second = 65; second <= 90; second += 1) {
        const code = String.fromCharCode(first, second)
        const display = names.of(code)
        if (!display || display === code) continue
        intlCountryNameMap[display.toLowerCase()] = code
      }
    }
  } catch {
    /* name-only rows stay without a code */
  }
  return intlCountryNameMap
}

function isoCodeFromValue(value) {
  const raw = String(value ?? "").trim().toUpperCase()
  return /^[A-Z]{2}$/.test(raw) ? raw : ""
}

/** Instant UI short code from a country name. No static country list. */
export function countryNameToCode(name) {
  const key = normalizePlaceKey(name)
  if (!key) return ""
  if (/^[a-z]{2}$/.test(key)) return key.toUpperCase()
  const stripped = key
    .replace(/^the\s+/, "")
    .replace(/^(republic of|islamic republic of)\s+/, "")
  return getIntlCountryNameMap()[key] || getIntlCountryNameMap()[stripped] || ""
}

/** Prefer API iso/code fields; otherwise derive the 2-letter code from the name. */
export function countryCodeFromApiRow(row, countryName) {
  const fromApi = isoCodeFromValue(
    row?.countryCode ??
      row?.country_code ??
      row?.code ??
      row?.isoCode ??
      row?.iso_code ??
      row?.iso2 ??
      row?.countryIso ??
      row?.country_iso,
  )
  if (fromApi) return fromApi
  return countryNameToCode(countryName ?? row?.name)
}

function rowMatchesPlatformFilter(row, socialNetworkTypes, platformKeysFilter) {
  if (platformKeysFilter == null) return true
  if (platformKeysFilter.length === 0) return false
  const chartKey = socialNetworkTypeIdToChartKey(
    socialNetworkTypes,
    row?.socialNetworkTypeId,
  )
  return chartKey != null && platformKeysFilter.includes(chartKey)
}

function filterDemographicRows(rows, socialNetworkTypes, platformKeysFilter) {
  const safeRows = Array.isArray(rows) ? rows : []
  return safeRows.filter((row) =>
    rowMatchesPlatformFilter(row, socialNetworkTypes, platformKeysFilter),
  )
}

function parseGenderAgeRow(name) {
  const decoded = decodeDemographicName(name)
  const match = /^([MFU])_(.+)$/.exec(decoded)
  if (!match) return null
  const genderCode = match[1]
  const age = match[2]
  if (genderCode === "U") return null
  return {
    gender: genderCode === "F" ? "female" : "male",
    age,
  }
}

const PLATFORM_DISPLAY_ORDER = ["facebook", "instagram", "linkedin", "twitter", "tiktok"]

function sortPlatformKeys(keys) {
  return [...keys].sort((a, b) => {
    const ia = PLATFORM_DISPLAY_ORDER.indexOf(a)
    const ib = PLATFORM_DISPLAY_ORDER.indexOf(b)
    if (ia === -1 && ib === -1) return String(a).localeCompare(String(b))
    if (ia === -1) return 1
    if (ib === -1) return -1
    return ia - ib
  })
}

function collectDemographicPlatformKeys(rows, socialNetworkTypes, type) {
  const keys = new Set()
  for (const row of rows) {
    if (Number(row?.type) !== type) continue
    const followers = Number(row.totalFollowers) || 0
    if (followers <= 0) continue
    const platformKey = socialNetworkTypeIdToChartKey(
      socialNetworkTypes,
      row?.socialNetworkTypeId,
    )
    if (platformKey) keys.add(platformKey)
  }
  return sortPlatformKeys([...keys])
}

export function buildAgeGenderFromRows(rows) {
  const buckets = new Map()

  for (const row of rows) {
    if (Number(row?.type) !== DEMOGRAPHIC_TYPE.GENDER_AGE) continue
    const parsed = parseGenderAgeRow(row.name)
    if (!parsed) continue

    const followers = Number(row.totalFollowers) || 0
    if (followers <= 0) continue

    if (!buckets.has(parsed.age)) {
      buckets.set(parsed.age, { age: parsed.age, female: 0, male: 0 })
    }
    const bucket = buckets.get(parsed.age)
    bucket[parsed.gender] += followers
  }

  return AGE_BUCKETS.filter((age) => buckets.has(age)).map((age) => buckets.get(age))
}

export function buildTopCitiesFromRows(rows, limit = 5) {
  const totals = new Map()

  for (const row of rows) {
    if (Number(row?.type) !== DEMOGRAPHIC_TYPE.CITY) continue
    const city = decodeDemographicName(row.name)
    if (!city || isNumericPlaceName(city)) continue

    const key = normalizePlaceKey(city)
    const followers = Number(row.totalFollowers) || 0
    if (followers <= 0) continue

    const existing = totals.get(key)
    if (existing) {
      existing.value += followers
    } else {
      totals.set(key, { city, value: followers })
    }
  }

  return [...totals.values()]
    .sort((a, b) => b.value - a.value)
    .slice(0, limit)
}

/** Top cities with per-platform counts (e.g. Facebook + Instagram grouped bars). */
export function buildTopCitiesByPlatformFromRows(
  rows,
  socialNetworkTypes,
  limit = 5,
  platformKeysFilter = ['facebook', 'instagram'],
) {
  const cityMap = new Map()
  const platformKeys = new Set(platformKeysFilter)

  for (const row of rows) {
    if (Number(row?.type) !== DEMOGRAPHIC_TYPE.CITY) continue
    const city = decodeDemographicName(row.name)
    if (!city || isNumericPlaceName(city)) continue

    const platformKey = socialNetworkTypeIdToChartKey(
      socialNetworkTypes,
      row?.socialNetworkTypeId,
    )
    if (!platformKey || !platformKeys.has(platformKey)) continue

    const followers = Number(row.totalFollowers) || 0
    if (followers <= 0) continue

    const key = normalizePlaceKey(city)
    if (!cityMap.has(key)) {
      cityMap.set(key, { city, total: 0 })
    }
    const entry = cityMap.get(key)
    entry[platformKey] = (entry[platformKey] || 0) + followers
    entry.total += followers
  }

  const sortedPlatformKeys = [...platformKeys].sort()
  return [...cityMap.values()]
    .sort((a, b) => b.total - a.total)
    .slice(0, limit)
    .map(({ city, total: _total, ...platformCounts }) => {
      const row = { city }
      for (const platformKey of sortedPlatformKeys) {
        row[platformKey] = platformCounts[platformKey] || 0
      }
      return row
    })
}

export function buildTopCountriesByPlatformFromRows(
  rows,
  socialNetworkTypes,
  limit = 5,
) {
  const countryMap = new Map()
  const platformKeys = new Set()

  for (const row of rows) {
    if (Number(row?.type) !== DEMOGRAPHIC_TYPE.COUNTRY) continue
    const country = decodeDemographicName(row.name)
    if (!country || isNumericPlaceName(country)) continue

    const platformKey = socialNetworkTypeIdToChartKey(
      socialNetworkTypes,
      row?.socialNetworkTypeId,
    )
    if (!platformKey) continue

    const followers = Number(row.totalFollowers) || 0
    if (followers <= 0) continue

    platformKeys.add(platformKey)
    const key = normalizePlaceKey(country)
    if (!countryMap.has(key)) {
      countryMap.set(key, { country, total: 0 })
    }
    const entry = countryMap.get(key)
    entry[platformKey] = (entry[platformKey] || 0) + followers
    entry.total += followers
  }

  const sortedPlatformKeys = [...platformKeys].sort()
  return [...countryMap.values()]
    .sort((a, b) => b.total - a.total)
    .slice(0, limit)
    .map(({ country, total: _total, ...platformCounts }) => {
      const row = { country }
      for (const platformKey of sortedPlatformKeys) {
        row[platformKey] = platformCounts[platformKey] || 0
      }
      return row
    })
}

export function buildGeographicFromRows(rows) {
  const totals = new Map()

  for (const row of rows) {
    if (Number(row?.type) !== DEMOGRAPHIC_TYPE.COUNTRY) continue
    const country = decodeDemographicName(row.name)
    if (!country || isNumericPlaceName(country)) continue

    const followers = Number(row.totalFollowers) || 0
    if (followers <= 0) continue

    const key = normalizePlaceKey(country)
    const existing = totals.get(key)
    if (existing) {
      existing.count += followers
    } else {
      totals.set(key, {
        country,
        code: countryCodeFromApiRow(row, country),
        count: followers,
      })
    }
  }

  return [...totals.values()].sort((a, b) => b.count - a.count)
}

/**
 * Transform GET …/demographics/followers rows into dashboard audience insight shapes.
 */
export function buildAudienceInsightsFromDemographicsRows(
  rows,
  socialNetworkTypes = [],
  options = {},
) {
  const { platformKeysFilter = null } = options
  const filteredRows = filterDemographicRows(rows, socialNetworkTypes, platformKeysFilter)

  const ageGender = buildAgeGenderFromRows(filteredRows)
  const geographic = buildGeographicFromRows(filteredRows)
  const topCities = buildTopCitiesByPlatformFromRows(filteredRows, socialNetworkTypes, 5)
  const topCountries = buildTopCountriesByPlatformFromRows(
    filteredRows,
    socialNetworkTypes,
    5,
  )
  const ageGenderPlatformKeys = collectDemographicPlatformKeys(
    filteredRows,
    socialNetworkTypes,
    DEMOGRAPHIC_TYPE.GENDER_AGE,
  )

  const hasData =
    ageGender.length > 0 ||
    geographic.length > 0 ||
    topCities.length > 0 ||
    topCountries.length > 0

  return {
    hasData,
    demographicData: hasData ? { ageGender, geographic } : null,
    topCities,
    topCountries,
    ageGenderPlatformKeys,
  }
}

export function buildTopCitiesInsight(topCities) {
  if (!topCities?.length) {
    return "Review city-level follower data to plan geo-targeted content."
  }
  const names = topCities.slice(0, 2).map((row) => row.city)
  if (names.length === 1) {
    return `${names[0]} leads your urban audience. Consider geo-targeted content for this market.`
  }
  return `${names.join(" and ")} drive the majority of your urban audience. Consider geo-targeted content for these markets.`
}

const DEFAULT_COUNTRY_PLATFORM_LABELS = {
  facebook: "Facebook",
  instagram: "Instagram",
}

function joinChannelNames(names, emptyFallback = "platforms") {
  if (!names.length) return emptyFallback
  if (names.length === 1) return names[0]
  if (names.length === 2) return `${names[0]} and ${names[1]}`
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`
}

function channelNamesFromKeys(keys, platformLabels = {}) {
  const labels = { ...DEFAULT_COUNTRY_PLATFORM_LABELS, ...platformLabels }
  return keys.map((key) => labels[key] || key.charAt(0).toUpperCase() + key.slice(1))
}

export const AUDIENCE_AGE_GENDER_SOURCE_LABEL = "Age and Gender data from Facebook"
export const AUDIENCE_INSIGHTS_INFO_TOOLTIP =
  "Age and Gender data from Facebook. Hometown city and country data from Facebook and Instagram."

export function buildAgeGenderSourceLabel() {
  return AUDIENCE_AGE_GENDER_SOURCE_LABEL
}

export function buildAgeGenderInsight(ageGender, platformKeys = [], platformLabels = {}) {
  if (!ageGender?.length) return null
  const totalFemale = ageGender.reduce((sum, row) => sum + (row.female || 0), 0)
  const totalMale = ageGender.reduce((sum, row) => sum + (row.male || 0), 0)
  const total = totalFemale + totalMale
  if (total === 0) return null

  const femalePct = (totalFemale / total) * 100
  const dominantGender = femalePct >= 50 ? "female" : "male"
  const dominantAgeRow = ageGender.reduce(
    (best, row) => {
      const rowTotal = (row.female || 0) + (row.male || 0)
      return rowTotal > (best.total || 0) ? { ...row, total: rowTotal } : best
    },
    { total: 0 },
  )
  const channelNames = channelNamesFromKeys(sortPlatformKeys(platformKeys), platformLabels)
  const audienceLabel = channelNames.length
    ? `${joinChannelNames(channelNames)} audience`
    : "Audience"
  const share = (dominantGender === "female" ? femalePct : 100 - femalePct).toFixed(0)
  return `${audienceLabel} is ${share}% ${dominantGender} (${dominantAgeRow.age || "25-34"} dominant).`
}

export function buildTopCountriesInsight(topCountries, platformLabels = {}) {
  if (!topCountries?.length) {
    return "Review country-level follower distribution to focus engagement on your strongest markets."
  }
  const leader = topCountries[0]?.country
  if (!leader) {
    return "Review country-level follower distribution to focus engagement on your strongest markets."
  }
  const platformKeys = sortPlatformKeys(
    Object.keys(topCountries[0]).filter((key) => {
      if (key === "country" || key === "total") return false
      return topCountries.some((row) => Number(row[key]) > 0)
    }),
  )
  const channelNames = channelNamesFromKeys(platformKeys, platformLabels)
  return `${leader} leads follower distribution across ${joinChannelNames(channelNames)}. Focus engagement efforts on your strongest geographic markets.`
}

/** Keep only platform columns present in `platformKeys` (connected channels). */
export function filterTopCountriesRowsByPlatformKeys(rows, platformKeys) {
  if (!Array.isArray(rows) || !platformKeys?.length) return rows ?? []
  return rows.map((row) => {
    const next = { country: row.country }
    platformKeys.forEach((key) => {
      next[key] = row[key] ?? 0
    })
    return next
  })
}

export function geographicFollowerCount(row) {
  return Number(row?.count ?? row?.value ?? row?.followers) || 0
}

/** Match AudienceGeographicDistribution follower % (share of all countries, not a page subset). */
export function formatDemographicsFollowerShare(count, total) {
  const n = Number(count) || 0
  if (n <= 0) return "0.0%"
  if (!(total > 0)) return "—"
  const pct = (n / total) * 100
  if (pct >= 0.1) return `${pct.toFixed(1)}%`
  if (pct >= 0.01) return `${pct.toFixed(2)}%`
  return `${Math.max(pct, 0.001).toFixed(3)}%`
}

export function buildGeographicInsight(geographic) {
  if (!geographic?.length) {
    return "Review geographic follower distribution to optimize post timing and campaign targeting."
  }
  const top = geographic.slice(0, 3).map((row) => row.country)
  if (top.length === 1) {
    return `Your audience is concentrated in ${top[0]}. Consider time-zone optimization for posts and campaigns.`
  }
  return `Your audience is concentrated in ${top.slice(0, -1).join(", ")} and ${top[top.length - 1]}. Consider time-zone optimization for posts and campaigns.`
}
